import { useRef, useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Bot, User } from "lucide-react";

const suggestions = [
  "How should I sleep before my next night shift?",
  "I have 3 nights in a row starting tomorrow. Plan for me.",
  "When should I stop caffeine today?",
  "How do I recover on my first day off?",
];

export default function AiCircadianAdvisor() {
  const [msgs, setMsgs] = useState([]); // {role, text}
  const [q, setQ] = useState("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);

  const ask = async (question, attempt = 0) => {
    if (!question.trim() || streaming) return;
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    setQ("");
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${API}/ai/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Sorry — unable to respond right now." }; return c; });
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: buf }; return c; });
      }
      // Rate-limit auto-retry: if server surfaced a concurrent-request error, retry once after a short wait
      if (/concurrent_request_limit|RateLimit|429/i.test(buf) && attempt < 1) {
        setStreaming(false);
        await new Promise((r) => setTimeout(r, 1500));
        return ask(question, attempt + 1);
      }
    } catch (e) {
      // aborted or network err
    } finally {
      setStreaming(false);
    }
  };

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-indigo-300" /> Circa · AI Advisor
        </h2>
        <p className="text-slate-400 text-sm mt-1">Powered by Claude Sonnet 5. Personalized to your shifts and sleep history.</p>
      </div>

      <div className="circa-card p-5 sm:p-6 min-h-[420px] flex flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto max-h-[55vh] pr-2" data-testid="ai-chat-messages">
          {msgs.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-7 h-7 text-indigo-300" />
              </div>
              <div className="font-display text-lg text-slate-200 mb-1">Ask about your sleep, shifts, or recovery.</div>
              <div className="text-slate-500 text-sm">Or try one of these:</div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => ask(s)}
                    data-testid={`ai-suggestion-${s.slice(0,10)}`}
                    className="pill bg-slate-900/60 border-white/10 text-slate-300 hover:border-indigo-400/40 hover:text-indigo-200 normal-case tracking-normal font-normal font-sans text-xs"
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-indigo-300" /></div>}
              <div className={`${m.role === "user" ? "bg-indigo-500/15 border-indigo-400/30" : "bg-slate-900/60 border-white/10"} border rounded-2xl px-4 py-3 max-w-[80%] whitespace-pre-wrap text-sm leading-relaxed`}>
                {m.text || <span className="text-slate-500">…</span>}
              </div>
              {m.role === "user" && <div className="w-8 h-8 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-slate-300" /></div>}
            </div>
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); ask(q); }}
          className="mt-4 flex gap-2 items-end"
        >
          <Textarea
            data-testid="ai-input-question"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ask about caffeine timing, naps, jet lag…"
            rows={2}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(q); } }}
            className="bg-slate-900/60 border-white/10 text-slate-100 resize-none"
          />
          <Button
            type="submit"
            disabled={streaming || !q.trim()}
            data-testid="ai-submit-question"
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-11 px-5 glow-indigo"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </section>
  );
}
